// SupplierForm - Create/Edit supplier
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, FormProvider } from 'react-hook-form'
import SubHeaders from '@/components/Headers/SubHeaders'
import { paths } from '@/utils/path'
import { Button, FormItem } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { getSupplier, createSupplier, updateSupplier } from '../../services'
import { CreateSupplierRequest } from '../../types/request'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface SupplierFormData {
    code: string
    name: string
    contact_person: string
    email: string
    phone: string
    address: string
    tax_code: string
    website: string
    notes: string
    is_active: boolean
}

const SupplierForm = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const isEditMode = Boolean(id)

    const methods = useForm<SupplierFormData>({
        defaultValues: {
            code: '',
            name: '',
            contact_person: '',
            email: '',
            phone: '',
            address: '',
            tax_code: '',
            website: '',
            notes: '',
            is_active: true,
        },
    })

    const { control, handleSubmit, reset, watch, setValue } = methods
    const watchedIsActive = watch('is_active')

    // Fetch supplier data for edit mode
    const { data: supplierData, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_SUPPLIERS, id],
        queryFn: () => getSupplier(id!),
        enabled: isEditMode,
    })

    useEffect(() => {
        if (supplierData) {
            reset({
                code: supplierData.code,
                name: supplierData.name,
                contact_person: supplierData.contact_person || '',
                email: supplierData.email || '',
                phone: supplierData.phone || '',
                address: supplierData.address || '',
                tax_code: supplierData.tax_code || '',
                website: supplierData.website || '',
                notes: supplierData.notes || '',
                is_active: supplierData.is_active,
            })
        }
    }, [supplierData, reset])

    // Create mutation
    const { mutateAsync: submitCreate, isPending: isCreating } =
        useMutationWithHandler(
            {
                mutationFn: (data: CreateSupplierRequest) =>
                    createSupplier(data),
            },
            {
                successMessage: 'Supplier created successfully',
                reactQueryKeys: QUERY_KEYS.WAREHOUSE_SUPPLIERS,
                onSuccess: () => {
                    navigate(paths.warehouseSuppliers)
                },
            }
        )

    // Update mutation
    const { mutateAsync: submitUpdate, isPending: isUpdating } =
        useMutationWithHandler(
            {
                mutationFn: (data: CreateSupplierRequest) =>
                    updateSupplier(id!, data),
            },
            {
                successMessage: 'Supplier updated successfully',
                reactQueryKeys: QUERY_KEYS.WAREHOUSE_SUPPLIERS,
                onSuccess: () => {
                    navigate(paths.warehouseSuppliers)
                },
            }
        )

    const isPending = isCreating || isUpdating

    const onSubmit = async (data: SupplierFormData) => {
        const payload: CreateSupplierRequest = {
            code: data.code,
            name: data.name,
            contact_person: data.contact_person || undefined,
            email: data.email || undefined,
            phone: data.phone || undefined,
            address: data.address || undefined,
            tax_code: data.tax_code || undefined,
            website: data.website || undefined,
            notes: data.notes || undefined,
            is_active: data.is_active,
        }

        if (isEditMode) {
            await submitUpdate(payload)
        } else {
            await submitCreate(payload)
        }
    }

    if (isEditMode && isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">Loading supplier...</div>
            </div>
        )
    }

    return (
        <div>
            <SubHeaders
                breadcrumbs={[
                    { path: paths.warehouse, title: 'Capacities & Resources' },
                    { path: paths.warehouseSuppliers, title: 'Suppliers' },
                    {
                        path: '',
                        title: isEditMode ? 'Edit Supplier' : 'New Supplier',
                    },
                ]}
            />

            <FormProvider {...methods}>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="space-y-2 mt-1"
                >
                    {/* Basic Info Section */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Supplier Information</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormItem
                                    control={control}
                                    name="code"
                                    label="Supplier Code *"
                                    type="textfield"
                                    placeholder="e.g., SUP001"
                                    rules={{
                                        required: 'Supplier code is required',
                                    }}
                                />

                                <FormItem
                                    control={control}
                                    name="name"
                                    label="Supplier Name *"
                                    type="textfield"
                                    placeholder="e.g., Dell Technologies"
                                    rules={{
                                        required: 'Supplier name is required',
                                    }}
                                />

                                <FormItem
                                    control={control}
                                    name="contact_person"
                                    label="Contact Person"
                                    type="textfield"
                                    placeholder="e.g., John Doe"
                                />

                                <FormItem
                                    control={control}
                                    name="email"
                                    label="Email"
                                    type="textfield"
                                    placeholder="e.g., contact@supplier.com"
                                    rules={{
                                        pattern: {
                                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                            message: 'Invalid email format',
                                        },
                                    }}
                                />

                                <FormItem
                                    control={control}
                                    name="phone"
                                    label="Phone"
                                    type="textfield"
                                    placeholder="e.g., +84 123 456 789"
                                />

                                <FormItem
                                    control={control}
                                    name="tax_code"
                                    label="Tax Code"
                                    type="textfield"
                                    placeholder="e.g., 0123456789"
                                />

                                <FormItem
                                    control={control}
                                    name="website"
                                    label="Website"
                                    type="textfield"
                                    placeholder="e.g., https://www.supplier.com"
                                />

                                <div className="space-y-2">
                                    <Label>Status</Label>
                                    <div className="flex items-center gap-2 h-9">
                                        <Switch
                                            checked={watchedIsActive}
                                            onCheckedChange={(checked) =>
                                                setValue('is_active', checked)
                                            }
                                        />
                                        <span className="text-sm">
                                            {watchedIsActive
                                                ? 'Active'
                                                : 'Inactive'}
                                        </span>
                                    </div>
                                </div>

                                <div className="md:col-span-2">
                                    <FormItem
                                        control={control}
                                        name="address"
                                        label="Address"
                                        type="textarea"
                                        placeholder="Full address..."
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <FormItem
                                        control={control}
                                        name="notes"
                                        label="Notes"
                                        type="textarea"
                                        placeholder="Additional notes..."
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Form Actions */}
                    <div className="flex gap-2 justify-end">
                        <Button
                            variant="outlined"
                            type="button"
                            onClick={() => navigate(paths.warehouseSuppliers)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            loading={isPending}
                        >
                            {isEditMode ? 'Update Supplier' : 'Create Supplier'}
                        </Button>
                    </div>
                </form>
            </FormProvider>
        </div>
    )
}

export default SupplierForm
